class KenzeCardPurchase extends HTMLElement {
  connectedCallback() {
    this.card = this.closest('product-card, [data-kenze-restock-card]');
    this.form = this.querySelector('[data-kenze-buy-now-form]');
    this.variantInput = this.querySelector('[data-kenze-variant-id]');
    this.buyButton = this.querySelector('[data-kenze-card-buy]');
    this.status = this.querySelector('[data-kenze-buy-now-status]');
    this.restockButton = this.querySelector('[data-kenze-card-restock]');
    KenzeCardPurchase.choiceIndex = (KenzeCardPurchase.choiceIndex || 0) + 1;
    for (const radio of this.querySelectorAll('.kenze-card-purchase__fallback-swatches input[type="radio"]')) radio.name = `kenze-card-choice-${KenzeCardPurchase.choiceIndex}`;

    if (!this.card || !this.form || !this.variantInput || !this.buyButton) return;

    this.handleChange = this.handleChange.bind(this);
    this.handleBuy = this.handleBuy.bind(this);

    // Capture card option changes so Buy now, media, links and price stay in sync.
    this.card.addEventListener('change', this.handleChange, true);
    this.form.addEventListener('submit', this.handleBuy);

    const selectedSwatch = this.card.querySelector('input[type="radio"]:checked[data-variant-id]');
    const genericSelect = this.querySelector('[data-kenze-variant-select]');
    this.setupVariantMenu(genericSelect);

    if (selectedSwatch) {
      this.selectVariant(selectedSwatch);
    } else if (genericSelect instanceof HTMLSelectElement && genericSelect.value) {
      this.selectVariant(genericSelect.selectedOptions[0]);
    } else if (this.dataset.initialSelected === 'true') {
      this.variantInput.value = this.dataset.initialVariant;
      this.selectVariant({ dataset: { variantId: this.dataset.initialVariant, hasAvailableVariant: this.dataset.initialAvailable }, getAttribute: () => this.dataset.initialLabel || null, value: this.dataset.initialVariant });
    } else if (this.dataset.requiresChoice !== 'true' && this.variantInput.value) {
      this.setReady(this.dataset.initialAvailable !== 'false');
      this.syncRestock(this.variantInput.value, this.dataset.initialAvailable !== 'false');
    }
  }

  disconnectedCallback() {
    this.destroyVariantMenu?.();
    this.card?.removeEventListener('change', this.handleChange, true);
    this.form?.removeEventListener('submit', this.handleBuy);
  }

  setupVariantMenu(select) {
    this.destroyVariantMenu?.();
    if (!(select instanceof HTMLSelectElement) || !HTMLElement.prototype.showPopover) return;
    const controller = new AbortController();
    const on = (target, type, listener, options = {}) => target.addEventListener(type, listener, { ...options, signal: controller.signal });
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'kenze-card-purchase__variant-select kenze-card-purchase__variant-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    const menu = document.createElement('div');
    menu.id = `kenze-card-options-${KenzeCardPurchase.choiceIndex}`;
    menu.className = 'kenze-card-purchase__variant-menu';
    menu.setAttribute('popover', 'auto');
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', select.getAttribute('aria-label') || '');
    trigger.setAttribute('aria-controls', menu.id);
    const choices = Array.from(select.options).filter(option => !option.disabled).map(option => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'kenze-card-purchase__variant-choice';
      button.setAttribute('role', 'option');
      button.tabIndex = -1;
      button.textContent = option.textContent.trim();
      on(button, 'click', event => {
        event.preventDefault();
        event.stopPropagation();
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
        close(true);
      });
      menu.append(button);
      return { option, button };
    });
    const sync = () => {
      trigger.textContent = select.selectedOptions[0]?.textContent.trim() || '';
      trigger.setAttribute('aria-label', `${select.getAttribute('aria-label') || ''}: ${trigger.textContent}`);
      choices.forEach(({ option, button }) => button.setAttribute('aria-selected', String(option.value === select.value)));
    };
    const close = (restoreFocus = false) => {
      if (menu.matches(':popover-open')) menu.hidePopover();
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger.focus({ preventScroll: true });
    };
    const open = (last = false) => {
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const spaceAbove = rect.top - 12;
      const above = spaceBelow < 140 && spaceAbove > spaceBelow;
      menu.style.width = `${Math.min(rect.width, window.innerWidth - 16)}px`;
      menu.style.maxHeight = `${Math.max(44, Math.min(264, above ? spaceAbove : spaceBelow))}px`;
      menu.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8))}px`;
      menu.showPopover();
      menu.style.top = `${above ? Math.max(8, rect.top - menu.offsetHeight - 4) : rect.bottom + 4}px`;
      trigger.setAttribute('aria-expanded', 'true');
      const selected = choices.find(({ option }) => option.value === select.value);
      (selected || choices[last ? choices.length - 1 : 0])?.button.focus({ preventScroll: true });
    };
    on(trigger, 'click', event => {
      event.preventDefault();
      event.stopPropagation();
      if (menu.matches(':popover-open')) close(true);
      else open();
    });
    on(trigger, 'keydown', event => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open(event.key === 'ArrowUp');
      }
    });
    on(menu, 'keydown', event => {
      const index = choices.findIndex(({ button }) => button === document.activeElement);
      let next;
      if (event.key === 'ArrowDown') next = (index + 1) % choices.length;
      if (event.key === 'ArrowUp') next = (index - 1 + choices.length) % choices.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = choices.length - 1;
      if (next !== undefined) {
        event.preventDefault();
        choices[next]?.button.focus();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      } else if (event.key === 'Tab') close(true);
    });
    on(menu, 'toggle', () => trigger.setAttribute('aria-expanded', String(menu.matches(':popover-open'))));
    on(select, 'change', sync);
    on(window, 'resize', () => close());
    on(document, 'scroll', event => { if (!menu.contains(event.target)) close(); }, { capture: true, passive: true });
    select.after(trigger, menu);
    select.hidden = true;
    sync();
    this.destroyVariantMenu = () => {
      controller.abort();
      close();
      trigger.remove();
      menu.remove();
      select.hidden = false;
      this.destroyVariantMenu = undefined;
    };
  }

  handleChange(event) {
    const input = event.target;

    if (input instanceof HTMLInputElement && input.matches('input[type="radio"][data-variant-id]')) {
      this.selectVariant(input);
      return;
    }

    if (input instanceof HTMLSelectElement && input.matches('[data-kenze-variant-select]')) {
      const option = input.selectedOptions[0];
      if (option) this.selectVariant(option);
    }
  }

  selectVariant(source) {
    const variantId = source.dataset.variantId || source.value || '';
    const available = source.dataset.hasAvailableVariant !== 'false' && source.dataset.optionAvailable !== 'false';
    const label = source.getAttribute('aria-label') || source.textContent?.trim() || (source.value !== variantId ? source.value : '') || '';

    this.variantInput.value = variantId;
    this.variantInput.disabled = !variantId || !available;

    // Keep the theme's native quick-add form in lockstep too. This makes the
    // media cart icon and our Buy now control always point at the same variant.
    const nativeQuickAddInput = this.card.querySelector('quick-add-component input[name="id"]');
    if (nativeQuickAddInput instanceof HTMLInputElement) {
      nativeQuickAddInput.value = variantId;
      nativeQuickAddInput.disabled = !variantId || !available;
    }
    const nativeQuickAdd = this.card.querySelector('quick-add-component');
    if (nativeQuickAdd) nativeQuickAdd.dataset.kenzeSelectedVariantId = variantId;

    // Update all visible card feedback immediately. Shopify's normal async
    // section refresh still runs afterward and reconciles the exact markup.
    this.syncVariantMedia(source);
    this.syncVariantPrice(source);

    // Keep every product-page link on the card synced to the chosen variant.
    if (variantId && typeof this.card.applyVariantToLinks === 'function') {
      this.card.applyVariantToLinks(variantId);
    } else if (variantId) {
      const link = this.card.querySelector('.resource-card__link[href]');
      if (link) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', variantId);
        link.href = url.toString();
      }
    }

    this.setReady(Boolean(variantId && available));
    this.syncRestock(variantId, available);
    if (this.status) {
      this.status.textContent = this.dataset.requiresChoice === 'true' && label
        ? (available ? `${label} selected.` : `${label} is currently unavailable.`)
        : (available ? 'Ready to buy now.' : 'This item is currently unavailable.');
    }
  }

  syncRestock(variantId, available) {
    if (!this.restockButton) return;
    this.restockButton.hidden = !variantId || available;
    this.restockButton.dataset.variantId = variantId;
    this.restockButton.dataset.productUrl = this.dataset.productUrl;
    this.form.hidden = this.dataset.purchaseExcluded === 'true' || Boolean(variantId && !available);
    this.classList.toggle('is-restock', Boolean(variantId && !available));
  }

  syncVariantPrice(source) {
    const price = source.dataset.variantPrice || '';
    if (!price) return;

    // A product card normally contains one product-price component. Update its
    // visible active price text synchronously so bundle/multi-pack swatches such
    // as “Get them all Offer” never leave the old price on screen while Shopify
    // finishes its section request.
    const priceContainer = this.card.querySelector('product-price [ref="priceContainer"]');
    if (!priceContainer) return;

    const priceNode = priceContainer.querySelector('.price:not(.compare-at-price)');
    if (!priceNode) return;

    const textNodes = Array.from(priceNode.childNodes).filter((node) => node.nodeType === Node.TEXT_NODE);
    if (textNodes.length) {
      textNodes[textNodes.length - 1].nodeValue = ` ${price}`;
    } else {
      priceNode.append(document.createTextNode(price));
    }
  }

  syncVariantMedia(source) {
    const mediaId = source.dataset.optionMediaId || '';
    const mediaSrc = source.dataset.optionMediaSrc || '';
    if (!mediaId && !mediaSrc) return;

    const gallery = this.card.querySelector('.card-gallery');
    const slideshow = gallery?.querySelector('slideshow-component');
    if (!gallery) {
      const image = this.card.querySelector('.resource-card__media img');
      if (image && mediaSrc) {
        image.srcset = '';
        image.src = mediaSrc;
      }
      return;
    }

    let targetSlide = null;
    if (mediaId) {
      targetSlide = Array.from(gallery.querySelectorAll('slideshow-slide')).find(
        (slide) => slide.getAttribute('slide-id') === mediaId
      );
    }

    if (targetSlide) {
      // Variant slides are pre-rendered but normally hidden. Reveal the selected
      // one synchronously so rapid taps cannot leave stale media.
      for (const slide of gallery.querySelectorAll('slideshow-slide[variant-image]')) {
        slide.hidden = slide !== targetSlide;
      }
      targetSlide.hidden = false;

      const image = targetSlide.querySelector('img');
      if (image) {
        image.removeAttribute('loading');
        image.setAttribute('fetchpriority', 'high');
      }

      if (slideshow && typeof slideshow.select === 'function') {
        try {
          slideshow.select({ id: mediaId }, undefined, { animate: false });
          return;
        } catch (error) {
          // Fall through to direct image replacement below.
        }
      }
    }

    // If Shopify omitted this media from the compact card slideshow, swap the
    // visible card image directly so the customer still gets instant feedback.
    if (mediaSrc) {
      const activeSlide = gallery.querySelector('slideshow-slide[aria-hidden="false"]') ||
        gallery.querySelector('slideshow-slide:not([hidden])') ||
        gallery.querySelector('slideshow-slide');
      const image = activeSlide?.querySelector('img');
      if (image) {
        image.removeAttribute('loading');
        image.setAttribute('fetchpriority', 'high');
        image.srcset = '';
        image.src = mediaSrc;
      }
    }
  }

  setReady(ready) {
    this.buyButton.disabled = !ready || this.dataset.purchaseExcluded === 'true';
    this.buyButton.textContent = 'Buy now';
    this.classList.toggle('is-ready', ready);
  }

  async handleBuy(event) {
    if (this.buyButton.disabled || !this.variantInput.value) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.buyButton.disabled = true;
    this.classList.add('is-loading');
    this.buyButton.textContent = 'Loading…';
    if (this.status) this.status.textContent = 'Preparing checkout…';

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(this.form),
      });

      if (!response.ok) throw new Error('Unable to add selected variant');
      const root = window.Shopify?.routes?.root || '/';
      window.location.assign(`${root.replace(/\/?$/, '/')}checkout`);
    } catch (error) {
      this.classList.remove('is-loading');
      this.buyButton.disabled = false;
      this.buyButton.textContent = 'Try again';
      if (this.status) this.status.textContent = 'Checkout could not start. Please try again.';
    }
  }
}

if (!customElements.get('kenze-card-purchase')) {
  customElements.define('kenze-card-purchase', KenzeCardPurchase);
}

document.addEventListener('click', async (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-kenze-product-buy-now]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const form = button.closest('form');
  const status = form?.querySelector('[data-kenze-product-buy-now-status]');
  if (!(form instanceof HTMLFormElement)) return;

  button.disabled = true;
  button.textContent = 'Preparing checkout…';
  if (status) status.textContent = 'Preparing checkout.';

  try {
    const response = await fetch(form.action, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
    });

    if (!response.ok) throw new Error('Unable to add selected variant');
    const root = window.Shopify?.routes?.root || '/';
    window.location.assign(`${root.replace(/\/?$/, '/')}checkout`);
  } catch (error) {
    button.disabled = false;
    button.textContent = 'Try again';
    if (status) status.textContent = 'Checkout could not start. Please try again.';
  }
});
